(() => {
  if (window.__MF_DESIGNER_MOUSE_ROTATION_V1__) return;
  window.__MF_DESIGNER_MOUSE_ROTATION_V1__ = true;

  const TARGET = [
    '[data-mfs] canvas[data-canvas]',
    '[data-mfh] canvas[data-canvas]',
    '[data-mfp] canvas[data-canvas]',
    '[data-mf-beanie-canvas]',
    '[data-mf-trucker-canvas]',
    '[data-mf-sl] canvas[data-canvas]'
  ].join(',');

  const states = new WeakMap();
  const activeStates = new Set();
  let syntheticSeed = 810000;

  const normColor = (v) => String(v || '').toLowerCase().replace(/\s+/g, '');
  const isTarget = (canvas) => canvas instanceof HTMLCanvasElement && canvas.matches(TARGET);
  const angleDelta = (a, b) => {
    let d = a - b;
    while (d > Math.PI) d -= Math.PI * 2;
    while (d < -Math.PI) d += Math.PI * 2;
    return d;
  };
  const normalizeDeg = (d) => {
    let out = d % 360;
    if (out > 180) out -= 360;
    if (out < -180) out += 360;
    return out;
  };

  function isImageLayerAllowed(canvas) {
    if (canvas.matches('[data-mf-beanie-canvas]')) {
      const root = canvas.closest('[data-mf-beanie-shell]');
      const active = root?.querySelector('[data-mf-beanie-layer].is-active');
      return !active || active.dataset.mfBeanieLayer === 'image';
    }
    if (canvas.matches('[data-mf-trucker-canvas]')) {
      const root = canvas.closest('[data-mf-trucker-shell]');
      const active = root?.querySelector('[data-mf-trucker-layer].is-active');
      return !active || active.dataset.mfTruckerLayer === 'image';
    }
    if (canvas.closest('[data-mf-sl]')) {
      const root = canvas.closest('[data-mf-sl]');
      const active = root?.querySelector('[data-layer].on');
      return !active || active.dataset.layer === 'image';
    }
    return true;
  }

  function isSelectionStroke(ctx) {
    if (!ctx || ctx.globalAlpha <= 0.05) return false;
    const c = normColor(ctx.strokeStyle);
    if (c === '#ff6a00' || c === 'rgb(255,106,0)' || c === 'rgba(255,106,0,1)') return true;
    if ((c === '#f58220' || c === 'rgb(245,130,32)' || c === 'rgba(245,130,32,1)') && Number(ctx.lineWidth) >= 5.5) return true;
    return false;
  }

  function transformPoint(m, x, y) {
    return {
      x: m.a * x + m.c * y + m.e,
      y: m.b * x + m.d * y + m.f
    };
  }

  function toClient(canvas, p) {
    const r = canvas.getBoundingClientRect();
    return {
      x: r.left + (p.x / canvas.width) * r.width,
      y: r.top + (p.y / canvas.height) * r.height
    };
  }

  function rootFor(canvas) {
    return canvas.closest('[data-mfs],[data-mfh],[data-mfp],[data-mf-beanie-shell],[data-mf-trucker-shell],[data-mf-sl]');
  }

  function ensureState(canvas) {
    let st = states.get(canvas);
    if (st) return st;

    const root = rootFor(canvas);
    if (!root) return null;
    const host = canvas.closest('dialog') || root;
    const handle = document.createElement('button');
    handle.type = 'button';
    handle.className = 'mf-mouse-rotate-handle';
    handle.setAttribute('aria-label', 'Motiv mit der Maus drehen');
    handle.title = 'Ziehen zum Drehen · 360°';
    handle.innerHTML = '<span aria-hidden="true">↻</span>';
    host.appendChild(handle);

    st = {
      canvas, root, host, handle, info: null,
      gen: 0, capturedGen: -1, rotating: false,
      mode: '', slider: null, lastAngle: 0, currentDeg: 0,
      centerClient: null, radius: 0, ids: null, lastSynthetic: null,
      realPointerId: null, controller: null
    };
    states.set(canvas, st);
    activeStates.add(st);

    handle.addEventListener('pointerdown', (e) => beginRotate(st, e));
    return st;
  }

  function updateHandle(st) {
    if (!st || st.rotating || !st.info || window.innerWidth <= 760) return;
    if (!st.canvas.isConnected || !st.root.isConnected) return hide(st);
    const cr = st.canvas.getBoundingClientRect();
    if (!cr.width || !cr.height) return hide(st);

    const center = toClient(st.canvas, st.info.center);
    const top = toClient(st.canvas, st.info.top);
    let vx = top.x - center.x, vy = top.y - center.y;
    const len = Math.hypot(vx, vy) || 1;
    vx /= len; vy /= len;
    const x = top.x + vx * 25;
    const y = top.y + vy * 25;

    st.handle.style.left = `${x}px`;
    st.handle.style.top = `${y}px`;
    st.handle.style.display = 'grid';
  }

  function hide(st) {
    if (st?.handle && !st.rotating) st.handle.style.display = 'none';
  }

  function captureSelection(ctx, x, y, w, h) {
    const canvas = ctx.canvas;
    if (!isTarget(canvas) || !isImageLayerAllowed(canvas) || !isSelectionStroke(ctx)) return;
    const st = ensureState(canvas);
    if (!st) return;
    const m = ctx.getTransform();
    st.info = {
      center: transformPoint(m, x + w / 2, y + h / 2),
      top: transformPoint(m, x + w / 2, y)
    };
    st.capturedGen = st.gen;
    if (!st.rotating) updateHandle(st);
  }

  const rawStrokeRect = CanvasRenderingContext2D.prototype.strokeRect;
  CanvasRenderingContext2D.prototype.strokeRect = function(x, y, w, h) {
    try { captureSelection(this, x, y, w, h); } catch (_) {}
    return rawStrokeRect.apply(this, arguments);
  };

  const rawClearRect = CanvasRenderingContext2D.prototype.clearRect;
  CanvasRenderingContext2D.prototype.clearRect = function(x, y, w, h) {
    const canvas = this.canvas;
    let st = null, gen = -1;
    if (isTarget(canvas)) {
      st = ensureState(canvas);
      if (st) { st.gen += 1; gen = st.gen; }
    }
    const out = rawClearRect.apply(this, arguments);
    if (st) {
      requestAnimationFrame(() => {
        if (!st.rotating && st.gen === gen && st.capturedGen !== gen) hide(st);
      });
    }
    return out;
  };

  function withCaptureDisabled(canvas, fn) {
    const hadSet = Object.prototype.hasOwnProperty.call(canvas, 'setPointerCapture');
    const hadRelease = Object.prototype.hasOwnProperty.call(canvas, 'releasePointerCapture');
    const ownSet = canvas.setPointerCapture;
    const ownRelease = canvas.releasePointerCapture;
    try {
      Object.defineProperty(canvas, 'setPointerCapture', { configurable: true, writable: true, value: () => {} });
      Object.defineProperty(canvas, 'releasePointerCapture', { configurable: true, writable: true, value: () => {} });
      fn();
    } finally {
      try {
        if (hadSet) Object.defineProperty(canvas, 'setPointerCapture', { configurable: true, writable: true, value: ownSet });
        else delete canvas.setPointerCapture;
        if (hadRelease) Object.defineProperty(canvas, 'releasePointerCapture', { configurable: true, writable: true, value: ownRelease });
        else delete canvas.releasePointerCapture;
      } catch (_) {}
    }
  }

  function synthetic(canvas, type, pointerId, x, y, buttons) {
    const e = new PointerEvent(type, {
      bubbles: true, cancelable: true, composed: true,
      pointerId, pointerType: 'pen', isPrimary: false,
      clientX: x, clientY: y,
      buttons: buttons ?? (type === 'pointerup' ? 0 : 1),
      button: type === 'pointerup' ? 0 : -1,
      pressure: type === 'pointerup' ? 0 : 0.5
    });
    canvas.dispatchEvent(e);
  }

  function beginNativeGesture(st, e) {
    const center = toClient(st.canvas, st.info.center);
    const a = Math.atan2(e.clientY - center.y, e.clientX - center.x);
    const radius = Math.max(52, Math.hypot(e.clientX - center.x, e.clientY - center.y));
    const p2 = { x: center.x + Math.cos(a) * radius, y: center.y + Math.sin(a) * radius };
    const id1 = ++syntheticSeed, id2 = ++syntheticSeed;

    st.centerClient = center;
    st.radius = radius;
    st.ids = [id1, id2];
    st.lastSynthetic = p2;

    withCaptureDisabled(st.canvas, () => {
      synthetic(st.canvas, 'pointerdown', id1, center.x, center.y, 1);
      synthetic(st.canvas, 'pointerdown', id2, p2.x, p2.y, 1);
    });
  }

  function moveNativeGesture(st, e) {
    if (!st.ids || !st.centerClient) return;
    const c = st.centerClient;
    const a = Math.atan2(e.clientY - c.y, e.clientX - c.x);
    const p2 = { x: c.x + Math.cos(a) * st.radius, y: c.y + Math.sin(a) * st.radius };
    st.lastSynthetic = p2;
    withCaptureDisabled(st.canvas, () => synthetic(st.canvas, 'pointermove', st.ids[1], p2.x, p2.y, 1));
  }

  function endNativeGesture(st) {
    if (!st.ids || !st.centerClient) return;
    const p2 = st.lastSynthetic || st.centerClient;
    withCaptureDisabled(st.canvas, () => {
      synthetic(st.canvas, 'pointerup', st.ids[1], p2.x, p2.y, 0);
      synthetic(st.canvas, 'pointerup', st.ids[0], st.centerClient.x, st.centerClient.y, 0);
    });
    st.ids = null; st.centerClient = null; st.lastSynthetic = null;
  }

  function beginRotate(st, e) {
    if (!st.info || window.innerWidth <= 760 || e.pointerType === 'touch') return;
    e.preventDefault();
    e.stopPropagation();

    if (st.controller) st.controller.abort();
    st.controller = new AbortController();
    st.rotating = true;
    st.realPointerId = e.pointerId;
    st.handle.classList.add('is-rotating');

    const center = toClient(st.canvas, st.info.center);
    st.centerClient = center;
    st.lastAngle = Math.atan2(e.clientY - center.y, e.clientX - center.x);

    const apparelRoot = st.canvas.closest('[data-mfs],[data-mfh],[data-mfp]');
    const slider = apparelRoot?.querySelector('[data-rot]');
    if (slider) {
      st.mode = 'slider';
      st.slider = slider;
      st.currentDeg = Number(slider.value || 0);
    } else {
      st.mode = 'gesture';
      st.slider = null;
      beginNativeGesture(st, e);
    }

    const move = (ev) => {
      if (!st.rotating || ev.pointerType !== 'mouse' || ev.pointerId !== st.realPointerId) return;
      ev.preventDefault();
      const c = st.centerClient || toClient(st.canvas, st.info.center);
      const a = Math.atan2(ev.clientY - c.y, ev.clientX - c.x);

      st.handle.style.left = `${ev.clientX}px`;
      st.handle.style.top = `${ev.clientY}px`;

      if (st.mode === 'slider' && st.slider) {
        st.currentDeg += angleDelta(a, st.lastAngle) * 180 / Math.PI;
        st.lastAngle = a;
        st.slider.value = String(normalizeDeg(st.currentDeg));
        st.slider.dispatchEvent(new Event('input', { bubbles: true }));
      } else if (st.mode === 'gesture') {
        moveNativeGesture(st, ev);
      }
    };

    const finish = (ev) => {
      if (!st.rotating) return;
      if (ev && ev.pointerType && ev.pointerType !== 'mouse') return;
      if (ev && ev.pointerId != null && ev.pointerId !== st.realPointerId) return;
      if (st.mode === 'gesture') endNativeGesture(st);
      if (st.mode === 'slider' && st.slider) st.slider.dispatchEvent(new Event('change', { bubbles: true }));
      st.rotating = false;
      st.realPointerId = null;
      st.handle.classList.remove('is-rotating');
      st.mode = '';
      st.controller?.abort();
      st.controller = null;
      requestAnimationFrame(() => updateHandle(st));
    };

    window.addEventListener('pointermove', move, { passive: false, signal: st.controller.signal });
    window.addEventListener('pointerup', finish, { signal: st.controller.signal });
    window.addEventListener('pointercancel', finish, { signal: st.controller.signal });
    window.addEventListener('blur', () => finish(), { once: true, signal: st.controller.signal });
  }

  const style = document.createElement('style');
  style.id = 'mf-designer-mouse-rotation-style';
  style.textContent = `
    .mf-mouse-rotate-handle{
      position:fixed;display:none;place-items:center;width:36px;height:36px;padding:0;
      transform:translate(-50%,-50%);z-index:2147483000;border:2px solid #fff;border-radius:50%;
      background:#f58220;color:#111214;box-shadow:0 4px 15px rgba(0,0,0,.42);
      font:900 23px/1 Arial,sans-serif;cursor:grab;touch-action:none;user-select:none;
    }
    .mf-mouse-rotate-handle:hover{filter:brightness(1.08);transform:translate(-50%,-50%) scale(1.06)}
    .mf-mouse-rotate-handle.is-rotating{cursor:grabbing;box-shadow:0 0 0 4px rgba(245,130,32,.24),0 5px 18px rgba(0,0,0,.5)}
    .mf-mouse-rotate-handle span{display:block;transform:translateY(-1px);pointer-events:none}
    @media(max-width:760px){.mf-mouse-rotate-handle{display:none!important}}
  `;
  if (!document.getElementById(style.id)) document.head.appendChild(style);

  const refresh = () => {
    activeStates.forEach((st) => {
      if (!st.canvas.isConnected || !st.root.isConnected) {
        st.handle?.remove(); activeStates.delete(st); return;
      }
      if (!st.rotating && st.info && st.capturedGen === st.gen) updateHandle(st);
    });
  };

  window.addEventListener('resize', refresh, { passive: true });
  document.addEventListener('scroll', refresh, { passive: true, capture: true });
  document.addEventListener('shopify:section:load', () => setTimeout(refresh, 50));
})();